import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Collapse,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ConfirmationNumber, ExpandLess, ExpandMore } from '@mui/icons-material';

import {
  RailJourney,
  RailOffer,
  fareLabel,
  formatDuration,
  formatLocalTime,
  railConnections,
  railJourneyProvider,
} from '../rail';
import { useAudience } from '../audience';
import { PriceBlock } from './PriceBlock';
import { FareConditionsList } from './FareConditionsList';
import { FareRow } from './FareRow';
import { ScheduleTimes } from './ScheduleTimes';
import { StatusFlag } from './StatusFlag';
import { RuteroResultCard } from './RuteroResultCard';

interface Props {
  journey: RailJourney;
  onBook: (fare: RailOffer) => void;
  selectionDisabled?: boolean;
  /** Por qué está bloqueada la selección (vencida, o búsqueda sin ejecutar). */
  selectionDisabledReason?: string;
  /** Cross-sell de pases sobre este viaje (opcional). */
  onSeePasses?: (fare: RailOffer) => void;
  /** Posición en la lista de resultados: da el escalonado de la entrada. */
  index?: number;
}

/**
 * Un viaje en tren con sus tarifas, en el arquetipo de SALIDAS: las horas son
 * el título —el asesor escanea por hora de salida, no por "Madrid →
 * Barcelona"— y el operador es una firma chica en la línea de identidad.
 *
 * Con más de una tarifa la comparación baja a la franja: una fila por tarifa
 * con su propio botón, y la columna de decisión muestra el "desde". Con una
 * sola tarifa no hay franja y el CTA vuelve a la columna, que es donde el
 * asesor lo busca en todas las verticales.
 */
export function RailJourneyCard({
  journey,
  onBook,
  selectionDisabled,
  selectionDisabledReason,
  onSeePasses,
  index,
}: Props) {
  const { t } = useTranslation();
  const { isAgent } = useAudience();
  /** Tarifa cuyas condiciones están abiertas (una a la vez). */
  const [openConditions, setOpenConditions] = useState<string | null>(null);

  const toggleConditions = (fareKey: string) =>
    setOpenConditions((prev) => (prev === fareKey ? null : fareKey));

  const connections = railConnections(journey);
  const [cheapest] = journey.fares;
  /**
   * `null` cuando las tarifas del viaje se compran por proveedores distintos:
   * ahí la atribución no puede ser del viaje, tiene que ser de cada fila.
   */
  const journeyProvider = railJourneyProvider(journey);
  const multiFare = journey.fares.length > 1;
  const [firstSegment] = journey.segments;

  /**
   * Qué tren es. Con una sola etapa el proveedor identifica el servicio
   * ("AVE 03093") y eso es lo que el pasajero busca en el andén; con
   * combinaciones no hay UN tren, así que la identidad es el operador.
   */
  const serviceName =
    journey.segments.length === 1
      ? [firstSegment?.transport, firstSegment?.identifier].filter(Boolean).join(' ')
      : '';

  const fareKeyOf = (fare: RailOffer, position: number) =>
    `${fare.provenance.slice(0, 12)}-${position}`;

  /**
   * Cuando el viaje mezcla proveedores, cada fila dice por dónde se reserva
   * ELLA: es la tarifa lo que se elige, no el viaje.
   */
  const fareProviderNote = (fare: RailOffer) =>
    isAgent && !journeyProvider
      ? `${t('travelServices.viaProvider', { provider: fare.provider })} · `
      : null;

  const conditionsLink = (fare: RailOffer, fareKey: string) => {
    if ((fare.fareConditions ?? []).length === 0) return null;
    const open = openConditions === fareKey;
    return (
      <Link
        component="button"
        type="button"
        variant="caption"
        underline="always"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, verticalAlign: 'baseline' }}
        onClick={() => toggleConditions(fareKey)}
      >
        {t('travelServices.rail.conditions')}
        {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
      </Link>
    );
  };

  const conditionsPanel = (fare: RailOffer, fareKey: string) => {
    const conditions = fare.fareConditions ?? [];
    if (conditions.length === 0) return null;
    return (
      // `timeout={0}`: abrir condiciones es una acción repetida y una
      // transición la vuelve lenta a la tercera vez.
      <Collapse in={openConditions === fareKey} unmountOnExit timeout={0}>
        <Box mt={0.5} textAlign="left">
          <FareConditionsList
            conditions={conditions}
            typeLabelPrefix="travelServices.rail.conditionTypes"
          />
        </Box>
      </Collapse>
    );
  };

  /** Pie de la franja: lo que aplica al viaje entero, no a una tarifa. */
  const stripFooter =
    onSeePasses || journey.extraVariants > 0 ? (
      <Box px="10px" py={0.75}>
        {journey.extraVariants > 0 && (
          <Typography variant="caption" color="text.secondary" display="block">
            {t('travelServices.rail.extraVariants', { count: journey.extraVariants })}
          </Typography>
        )}
        {onSeePasses && (
          <Tooltip title={t('travelServices.passes.crossSellHint')}>
            <Button
              size="small"
              startIcon={<ConfirmationNumber />}
              sx={{ mt: 0.25, textTransform: 'none' }}
              disabled={selectionDisabled}
              onClick={() => onSeePasses(journey.fares[0])}
            >
              {t('travelServices.passes.crossSellSearch')}
            </Button>
          </Tooltip>
        )}
      </Box>
    ) : null;

  const cheapestKey = fareKeyOf(cheapest, 0);

  return (
    <RuteroResultCard
      index={index}
      header={
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {journey.carrierLogoUrl ? (
            <Box
              component="img"
              src={journey.carrierLogoUrl}
              alt={journey.carrier}
              loading="lazy"
              sx={{ height: 18, maxWidth: 72, objectFit: 'contain' }}
            />
          ) : (
            <Typography
              component="span"
              sx={{
                fontFamily: 'monospace',
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                border: 1,
                borderColor: 'text.secondary',
                borderRadius: 0.5,
                px: 0.625,
                py: '1px',
              }}
            >
              {journey.carrier}
            </Typography>
          )}
          {serviceName && (
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
              {serviceName}
            </Typography>
          )}
          {/* Por dónde se compra es información de OPERACIÓN, no del viaje: el
              pasajero viaja con el operador (que sí queda a la vista), no con
              el conector del hub. En una superficie minorista desaparece. */}
          {isAgent && journeyProvider && (
            <Typography component="span" color="text.disabled" sx={{ fontSize: 11.5 }}>
              {t('travelServices.viaProvider', { provider: journeyProvider })}
            </Typography>
          )}
          {connections.length === 0 && (
            <Box ml="auto">
              <StatusFlag tone="ok" label={t('travelServices.rail.direct')} />
            </Box>
          )}
        </Stack>
      }
      title={
        <ScheduleTimes
          departure={{
            time: formatLocalTime(journey.departure),
            place: journey.fromStation,
          }}
          arrival={{
            time: formatLocalTime(journey.arrival),
            place: journey.toStation,
          }}
          duration={formatDuration(journey.departure, journey.arrival)}
          note={
            connections.length > 0
              ? [
                t('travelServices.rail.stops', { count: connections.length }),
                ...connections.map((connection) => `${connection.station} · ${connection.wait}`),
              ].join(' · ')
              : undefined
          }
        />
      }
      // Con una sola tarifa no hay nada que comparar en una franja: su
      // etiqueta y su letra chica viajan con el viaje.
      details={
        multiFare ? undefined : (
          <Box>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, mr: 0.5 }}>
              {fareLabel(cheapest)}
            </Typography>
            {conditionsLink(cheapest, cheapestKey)}
            {conditionsPanel(cheapest, cheapestKey)}
          </Box>
        )
      }
      price={
        <PriceBlock
          pricing={cheapest.pricing}
          fromLabel={multiFare ? t('travelServices.priceFrom') : undefined}
        />
      }
      action={
        multiFare ? undefined : (
          <Button
            variant="contained"
            sx={{ minWidth: 104, height: 30 }}
            disabled={selectionDisabled}
            onClick={() => onBook(cheapest)}
          >
            {t('travelServices.rail.select')}
          </Button>
        )
      }
      fares={
        multiFare || stripFooter ? (
          <>
            {multiFare &&
              journey.fares.map((fare, position) => {
                const fareKey = fareKeyOf(fare, position);
                return (
                  <FareRow
                    key={fareKey}
                    label={fareLabel(fare)}
                    detail={
                      <>
                        {fareProviderNote(fare)}
                        {conditionsLink(fare, fareKey)}
                      </>
                    }
                    pricing={fare.pricing}
                    actionLabel={t('travelServices.rail.select')}
                    onAction={() => onBook(fare)}
                    disabledReason={
                      selectionDisabled ? selectionDisabledReason : undefined
                    }
                  >
                    {conditionsPanel(fare, fareKey)}
                  </FareRow>
                );
              })}
            {stripFooter}
          </>
        ) : undefined
      }
    />
  );
}
